import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, ExternalLink, RefreshCw, Edit3 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

type Decision = "suppress" | "attach_existing" | "create_draft" | "escalate" | "pending";
const DECISIONS: Decision[] = ["pending", "escalate", "create_draft", "attach_existing", "suppress"];

const decisionColor: Record<string, string> = {
  pending: "bg-muted text-muted-foreground",
  escalate: "bg-yellow-100 text-yellow-900",
  create_draft: "bg-green-100 text-green-900",
  attach_existing: "bg-blue-100 text-blue-900",
  suppress: "bg-gray-200 text-gray-700",
};

type Source = {
  id: string;
  title: string | null;
  source_type: string;
  external_url: string | null;
  status: string;
  status_detail: string | null;
  ingested_at: string;
  triage_decision: Decision | null;
  triage_confidence: number | null;
  triage_model: string | null;
  triage_decision_at: string | null;
  triage_overridden: boolean | null;
};

// The triage agent writes status_detail as
//   "triage: <decision> (<conf>%) — <rationale>"
// (see research-triage-source). Pull the human-readable rationale back out so we can
// show the AI's reasoning cleanly, rather than the raw prefixed string.
function triageRationale(statusDetail: string | null | undefined): string | null {
  if (!statusDetail) return null;
  const m = statusDetail.match(/^\s*triage:\s*[^—-]*[—-]\s*(.+)$/s);
  const r = (m ? m[1] : statusDetail).trim();
  return r.length ? r : null;
}

const FILTERS: { value: string; label: string }[] = [
  { value: "needs_review", label: "Needs review" },
  { value: "pending", label: "Pending" },
  { value: "escalate", label: "Escalated" },
  { value: "low_confidence", label: "Low confidence (<70%)" },
  { value: "create_draft", label: "Create draft" },
  { value: "attach_existing", label: "Attach existing" },
  { value: "suppress", label: "Suppressed" },
  { value: "all", label: "All" },
];

export default function ResearchTriage() {
  const [filter, setFilter] = useState("needs_review");
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<Source | null>(null);
  const [newDecision, setNewDecision] = useState<Decision>("create_draft");
  const [reason, setReason] = useState("");
  const qc = useQueryClient();

  const { data: sources, isLoading } = useQuery({
    queryKey: ["research_sources", "triage"],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("research_sources")
        .select("id,title,source_type,external_url,status,status_detail,ingested_at,triage_decision,triage_confidence,triage_model,triage_decision_at,triage_overridden")
        .order("ingested_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data ?? []) as Source[];
    },
    refetchInterval: 15_000,
  });

  const filtered = useMemo(() => {
    const all = sources ?? [];
    let list = all;
    if (filter === "needs_review") {
      list = all.filter((s) => {
        const d = s.triage_decision ?? "pending";
        if (s.triage_overridden) return false;
        return d === "pending" || d === "escalate" || ((s.triage_confidence ?? 1) < 0.7);
      });
    } else if (filter === "low_confidence") {
      list = all.filter((s) => (s.triage_confidence ?? 1) < 0.7 && s.triage_decision != null && s.triage_decision !== "pending");
    } else if (filter !== "all") {
      list = all.filter((s) => (s.triage_decision ?? "pending") === filter);
    }
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter((s) => (s.title ?? "").toLowerCase().includes(needle) || (s.external_url ?? "").toLowerCase().includes(needle));
    }
    return list;
  }, [sources, filter, q]);

  const counts = useMemo(() => {
    const all = sources ?? [];
    const c: Record<string, number> = { needs_review: 0, low_confidence: 0, all: all.length };
    DECISIONS.forEach((d) => { c[d] = 0; });
    all.forEach((s) => {
      const d = s.triage_decision ?? "pending";
      c[d] = (c[d] ?? 0) + 1;
      if (!s.triage_overridden && (d === "pending" || d === "escalate" || ((s.triage_confidence ?? 1) < 0.7))) c.needs_review++;
      if ((s.triage_confidence ?? 1) < 0.7 && d !== "pending") c.low_confidence++;
    });
    return c;
  }, [sources]);

  const retriage = useMutation({
    mutationFn: async (source_id: string) => {
      const { data, error } = await supabase.functions.invoke("research-triage-source", { body: { source_id } });
      if (error) throw error;
      return data;
    },
    onSuccess: (data: any) => {
      toast.success(`Re-triaged: ${data?.decision ?? "ok"}`);
      qc.invalidateQueries({ queryKey: ["research_sources", "triage"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Triage failed"),
  });

  const override = useMutation({
    mutationFn: async ({ source, decision, reason }: { source: Source; decision: Decision; reason: string }) => {
      const { data: auth } = await supabase.auth.getUser();
      const { error: ovErr } = await (supabase as any).from("research_triage_overrides").insert({
        source_id: source.id,
        original_decision: source.triage_decision ?? "pending",
        override_decision: decision,
        override_reason: reason || null,
        overridden_by: auth.user?.id ?? null,
      });
      if (ovErr) throw ovErr;
      const { error: upErr } = await (supabase as any).from("research_sources").update({
        triage_decision: decision,
        triage_overridden: true,
        triage_decision_at: new Date().toISOString(),
      }).eq("id", source.id);
      if (upErr) throw upErr;
    },
    onSuccess: () => {
      toast.success("Override saved");
      setEditing(null);
      setReason("");
      qc.invalidateQueries({ queryKey: ["research_sources", "triage"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Override failed"),
  });

  const openOverride = (s: Source) => {
    setEditing(s);
    setNewDecision((s.triage_decision && s.triage_decision !== "pending" ? s.triage_decision : "create_draft") as Decision);
    setReason("");
  };

  // The source's input text is heavy, so it is NOT pulled into the 500-row list.
  // Fetch a short excerpt lazily, only for the one source being overridden, so the
  // reviewer can see what the AI actually triaged.
  const { data: editExcerpt } = useQuery({
    queryKey: ["research_source_excerpt", editing?.id],
    enabled: !!editing?.id,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("research_sources")
        .select("extracted_text")
        .eq("id", editing!.id)
        .maybeSingle();
      if (error) throw error;
      const t = (data?.extracted_text ?? "").trim();
      return t ? t.slice(0, 600) : null;
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold">Triage</h2>
        <p className="text-sm text-muted-foreground">
          Review and override the AI source-triage decisions. Overrides are stored as few-shot examples to improve future automatic triage.
        </p>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList className="flex-wrap h-auto">
            {FILTERS.map((f) => (
              <TabsTrigger key={f.value} value={f.value}>
                {f.label}
                {counts[f.value] != null && (
                  <Badge variant="secondary" className="ml-2 h-4 text-[10px]">{counts[f.value]}</Badge>
                )}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        <Input
          placeholder="Filter by title or URL…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-xs"
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">
            {filtered.length} source{filtered.length === 1 ? "" : "s"}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-36">Ingested</TableHead>
                <TableHead>Source</TableHead>
                <TableHead className="w-24">Type</TableHead>
                <TableHead className="w-40">Decision</TableHead>
                <TableHead className="w-20">Conf.</TableHead>
                <TableHead className="w-44 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
                  </TableCell>
                </TableRow>
              )}
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                    No entries
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((s) => {
                const d = (s.triage_decision ?? "pending") as Decision;
                const conf = s.triage_confidence;
                return (
                  <TableRow key={s.id}>
                    <TableCell className="text-xs whitespace-nowrap text-muted-foreground">
                      {format(new Date(s.ingested_at), "dd/MM/yyyy HH:mm")}
                    </TableCell>
                    <TableCell className="text-xs max-w-md">
                      <div className="font-medium truncate">{s.title ?? "Untitled"}</div>
                      {s.external_url && (
                        <a
                          href={s.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] text-muted-foreground hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          {s.external_url.replace(/^https?:\/\//, "").slice(0, 70)}
                        </a>
                      )}
                      {triageRationale(s.status_detail) && (
                        <div className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2 italic">
                          “{triageRationale(s.status_detail)}”
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{s.source_type}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`text-[10px] ${decisionColor[d]}`}>{d}</Badge>
                      {s.triage_overridden && (
                        <Badge variant="outline" className="ml-1 text-[10px]">override</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      <div>{conf != null ? `${Math.round(conf * 100)}%` : "—"}</div>
                      {s.triage_model && (
                        <div className="text-[10px] text-muted-foreground truncate max-w-[5rem]" title={s.triage_model}>
                          {s.triage_model}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" title="Re-run triage"
                          disabled={retriage.isPending}
                          onClick={() => retriage.mutate(s.id)}>
                          <RefreshCw className={`h-3 w-3 ${retriage.isPending && retriage.variables === s.id ? "animate-spin" : ""}`} />
                        </Button>
                        <Button size="sm" variant="ghost" title="Override decision"
                          onClick={() => openOverride(s)}>
                          <Edit3 className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override triage decision</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div>
                <div className="font-medium">{editing.title ?? "Untitled"}</div>
                <div className="text-xs text-muted-foreground">
                  Current: <Badge className={decisionColor[editing.triage_decision ?? "pending"]}>{editing.triage_decision ?? "pending"}</Badge>
                  {editing.triage_confidence != null && ` · ${Math.round(editing.triage_confidence * 100)}% confidence`}
                  {editing.triage_model && ` · ${editing.triage_model}`}
                </div>
              </div>

              {triageRationale(editing.status_detail) && (
                <div className="rounded-md border bg-muted/40 p-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    AI rationale
                  </div>
                  <p className="text-xs leading-relaxed">{triageRationale(editing.status_detail)}</p>
                </div>
              )}

              {editExcerpt && (
                <div className="rounded-md border bg-muted/20 p-2">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                    What the AI read (excerpt)
                  </div>
                  <p className="text-[11px] leading-relaxed text-muted-foreground whitespace-pre-wrap line-clamp-6">
                    {editExcerpt}
                  </p>
                </div>
              )}
              <div>
                <label className="text-xs font-medium">New decision</label>
                <Select value={newDecision} onValueChange={(v) => setNewDecision(v as Decision)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="create_draft">create_draft — promote to a research item draft</SelectItem>
                    <SelectItem value="attach_existing">attach_existing — link to an existing item</SelectItem>
                    <SelectItem value="escalate">escalate — needs human follow-up</SelectItem>
                    <SelectItem value="suppress">suppress — ignore this source</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium">Reason (used as few-shot example)</label>
                <Textarea
                  placeholder="Why is the AI wrong? e.g. 'Marketing email, no research signal' or 'Vontobel macro update — always draft'"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              disabled={override.isPending || !editing}
              onClick={() => editing && override.mutate({ source: editing, decision: newDecision, reason })}
            >
              {override.isPending && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
              Save override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
